import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography, Avatar, Chip, InputBase, IconButton, Button, Switch,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Pagination,
  FormControlLabel,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

import adminAxios from '../../utils/adminAxios';

// Paleta para las etiquetas de categoría (fallback cuando no tienen color guardado).
const PALETA = ['#FF9EBB', '#7495F8', '#FFD074', '#59C39A', '#9DE1D5', '#80A3FF', '#FF827A', '#67D5C4', '#B79CFF', '#F3A26D'];

const colorDe = (item, index) => item.color || PALETA[index % PALETA.length];

// Recalcula las métricas de las tarjetas a partir de los arreglos en memoria,
// para mantenerlas correctas sin volver a pedirlas al servidor.
const recomputarStats = (categorias, subcategorias) => ({
  totalCategorias: categorias.length,
  categoriasActivas: categorias.filter(c => c.status).length,
  totalSubcategorias: subcategorias.length,
  subcategoriasActivas: subcategorias.filter(s => s.status).length,
});

// Tarjeta-resumen superior.
const StatCard = ({ icon, bg, color, label, valor, pie }) => (
  <Paper sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', gap: 2 }}>
    <Avatar variant="rounded" sx={{ bgcolor: bg, color, width: 48, height: 48 }}>{icon}</Avatar>
    <Box>
      <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: 11 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 26, color: 'primary.main', lineHeight: 1.1 }}>{valor}</Typography>
      <Typography variant="caption" sx={{ color: '#94A3B8' }}>{pie}</Typography>
    </Box>
  </Paper>
);

const EstadoChip = ({ activo }) => (
  <Chip label={activo ? 'Activo' : 'Inactivo'} size="small"
    sx={{ height: 22, fontWeight: 700, bgcolor: activo ? '#D6F3E8' : '#FFE0DD', color: activo ? '#1B7A5A' : '#B23B33' }} />
);

// Cabecera de color plano para los modales (patrón del proyecto: sin gradientes).
const CabeceraDialog = ({ icon, titulo, sub, onClose }) => (
  <Box sx={{ bgcolor: 'secondary.main', color: 'white', px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Avatar variant="rounded" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 44, height: 44 }}>{icon}</Avatar>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{titulo}</Typography>
      <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>{sub}</Typography>
    </Box>
    <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}><CloseRoundedIcon /></IconButton>
  </Box>
);

const Catalogo = () => {
  const [tab, setTab] = useState('categorias');
  const [data, setData] = useState({ categorias: [], subcategorias: [], stats: null });
  const [q, setQ] = useState('');
  const [orden, setOrden] = useState({ campo: 'name', dir: 'asc' }); // ordenamiento de la tabla
  const [page, setPage] = useState(1);

  const LIMIT = 15;
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState(null);

  const [dialogCat, setDialogCat] = useState(null);   // { modo, _id?, name, descripcion, color }
  const [dialogSub, setDialogSub] = useState(null);   // { modo, _id?, name, descripcion, category }
  const [dialogBulk, setDialogBulk] = useState(null); // { texto, color }
  const [confirmDel, setConfirmDel] = useState(null); // { tipo, item }
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await adminAxios.get('/api/admin/catalogo');
      setData(res.data);
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo cargar el catálogo.' });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const stats = data.stats;

  // Color por categoría (estable por nombre) para reutilizar en subcategorías.
  const colorPorCategoria = useMemo(() => {
    const map = {};
    data.categorias.forEach((c, i) => { map[c._id] = colorDe(c, i); });
    return map;
  }, [data.categorias]);

  const rx = q.trim() ? new RegExp(q.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i') : null;

  const categoriasFiltradas = data.categorias.filter(c => !rx || rx.test(c.name) || rx.test(c.descripcion || ''));
  const subcategoriasFiltradas = data.subcategorias.filter(s => !rx || rx.test(s.name) || rx.test(s.categoriaNombre || '') || rx.test(s.descripcion || ''));

  // Ordenamiento por columna (clic en el encabezado alterna asc/desc).
  const ordenarPor = (campo) =>
    setOrden(o => ({ campo, dir: o.campo === campo && o.dir === 'asc' ? 'desc' : 'asc' }));

  const ordenar = (lista, getter) => {
    const arr = [...lista];
    arr.sort((a, b) => {
      const cmp = String(getter(a)).localeCompare(String(getter(b)), 'es', { sensitivity: 'base' });
      return orden.dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  };

  const categoriasOrdenadas = ordenar(categoriasFiltradas, c =>
    orden.campo === 'color' ? (colorPorCategoria[c._id] || '') : c.name);
  const subcategoriasOrdenadas = ordenar(subcategoriasFiltradas, s =>
    orden.campo === 'categoria' ? s.categoriaNombre : s.name);

  // Paginación (15 por página). Vuelve a la página 1 al cambiar tab, búsqueda u orden.
  useEffect(() => { setPage(1); }, [tab, q, orden]);

  const listaActual = tab === 'categorias' ? categoriasOrdenadas : subcategoriasOrdenadas;
  const totalActual = listaActual.length;
  const totalPaginas = Math.ceil(totalActual / LIMIT);
  const inicio = (page - 1) * LIMIT;
  const categoriasPagina = categoriasOrdenadas.slice(inicio, inicio + LIMIT);
  const subcategoriasPagina = subcategoriasOrdenadas.slice(inicio, inicio + LIMIT);

  // Encabezado de columna ordenable, con el estilo del catálogo.
  const HeaderOrden = ({ campo, children, align }) => (
    <TableCell sx={cabeceraSx} align={align} sortDirection={orden.campo === campo ? orden.dir : false}>
      <TableSortLabel
        active={orden.campo === campo}
        direction={orden.campo === campo ? orden.dir : 'asc'}
        onClick={() => ordenarPor(campo)}
        sx={{
          color: '#94A3B8 !important',
          '&:hover': { color: '#7495F8 !important' },
          '&.Mui-active': { color: '#7495F8 !important' },
          '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
        }}>
        {children}
      </TableSortLabel>
    </TableCell>
  );

  /* ---------- Acciones ---------- */

  const toggleCategoria = async (c) => {
    setData(prev => {
      const categorias = prev.categorias.map(x => x._id === c._id ? { ...x, status: !x.status } : x);
      return { ...prev, categorias, stats: recomputarStats(categorias, prev.subcategorias) };
    });
    try {
      await adminAxios.patch(`/api/category/status/${c._id}`, { status: c.status });
    } catch (e) {
      setData(prev => {
        const categorias = prev.categorias.map(x => x._id === c._id ? { ...x, status: c.status } : x);
        return { ...prev, categorias, stats: recomputarStats(categorias, prev.subcategorias) };
      });
      setToast({ tipo: 'error', msg: 'No se pudo cambiar el estado.' });
    }
  };

  // Alterna si la categoría exige cédula profesional. El servidor recalcula el bloqueo
  // de los servicios de la categoría (los oculta hasta que el proveedor valide su cédula).
  const toggleCedula = async (c) => {
    setData(prev => {
      const categorias = prev.categorias.map(x => x._id === c._id ? { ...x, requiereCedula: !x.requiereCedula } : x);
      return { ...prev, categorias };
    });
    try {
      await adminAxios.patch(`/api/category/cedula/${c._id}`, { requiereCedula: c.requiereCedula });
      setToast({ tipo: 'success', msg: !c.requiereCedula ? 'Ahora esta categoría exige cédula profesional.' : 'Esta categoría ya no exige cédula.' });
    } catch (e) {
      setData(prev => {
        const categorias = prev.categorias.map(x => x._id === c._id ? { ...x, requiereCedula: c.requiereCedula } : x);
        return { ...prev, categorias };
      });
      setToast({ tipo: 'error', msg: 'No se pudo cambiar la exigencia de cédula.' });
    }
  };

  const toggleSubcategoria = async (s) => {
    setData(prev => {
      const subcategorias = prev.subcategorias.map(x => x._id === s._id ? { ...x, status: !x.status } : x);
      return { ...prev, subcategorias, stats: recomputarStats(prev.categorias, subcategorias) };
    });
    try {
      await adminAxios.patch(`/api/category/subcategory/status/${s._id}`, { status: s.status });
    } catch (e) {
      setData(prev => {
        const subcategorias = prev.subcategorias.map(x => x._id === s._id ? { ...x, status: s.status } : x);
        return { ...prev, subcategorias, stats: recomputarStats(prev.categorias, subcategorias) };
      });
      setToast({ tipo: 'error', msg: 'No se pudo cambiar el estado.' });
    }
  };

  const guardarCategoria = async () => {
    const d = dialogCat;
    if (!d.name.trim()) { setErrores({ name: 'El nombre es obligatorio' }); return; }
    setGuardando(true);
    try {
      const payload = { name: d.name.trim(), descripcion: d.descripcion.trim(), color: d.color, requiereCedula: !!d.requiereCedula };
      if (d.modo === 'crear') {
        const res = await adminAxios.post('/api/category', payload);
        const nueva = { ...res.data, subcategorias: 0 };
        setData(prev => {
          const categorias = [...prev.categorias, nueva];
          return { ...prev, categorias, stats: recomputarStats(categorias, prev.subcategorias) };
        });
      } else {
        const res = await adminAxios.patch(`/api/category/${d._id}`, payload);
        setData(prev => {
          const categorias = prev.categorias.map(c => c._id === d._id
            ? { ...c, name: res.data.name, descripcion: res.data.descripcion, color: res.data.color, requiereCedula: res.data.requiereCedula } : c);
          // El nombre de la categoría está denormalizado en sus subcategorías: actualízalo también.
          const subcategorias = prev.subcategorias.map(s =>
            String(s.category) === String(d._id) ? { ...s, categoriaNombre: res.data.name } : s);
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
      }
      setDialogCat(null); setErrores({});
      setToast({ tipo: 'success', msg: d.modo === 'crear' ? 'Categoría creada.' : 'Categoría actualizada.' });
    } catch (e) {
      const msg = e.response?.data?.errors?.[0]?.msg || 'No se pudo guardar.';
      setErrores({ name: msg });
    } finally {
      setGuardando(false);
    }
  };

  const guardarSubcategoria = async () => {
    const d = dialogSub;
    const errs = {};
    if (!d.name.trim()) errs.name = 'El nombre es obligatorio';
    if (!d.category) errs.category = 'Selecciona una categoría';
    if (Object.keys(errs).length) { setErrores(errs); return; }
    setGuardando(true);
    try {
      const payload = { name: d.name.trim(), descripcion: d.descripcion.trim(), category: d.category };
      const catNombre = data.categorias.find(c => c._id === d.category)?.name || 'Sin categoría';
      if (d.modo === 'crear') {
        const res = await adminAxios.post('/api/category/subcategory', payload);
        const nueva = { ...res.data, category: d.category, categoriaNombre: catNombre };
        setData(prev => {
          const subcategorias = [...prev.subcategorias, nueva];
          const categorias = prev.categorias.map(c => c._id === d.category
            ? { ...c, subcategorias: (c.subcategorias || 0) + 1 } : c);
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
      } else {
        const res = await adminAxios.patch(`/api/category/subcategory/${d._id}`, payload);
        setData(prev => {
          const subcategorias = prev.subcategorias.map(s => s._id === d._id
            ? { ...s, name: res.data.name, descripcion: res.data.descripcion, category: d.category, categoriaNombre: catNombre } : s);
          // Si cambió de categoría, ajusta el conteo de subcategorías de la anterior y la nueva.
          let categorias = prev.categorias;
          if (d.categoryOriginal && d.categoryOriginal !== d.category) {
            categorias = prev.categorias.map(c => {
              if (c._id === d.categoryOriginal) return { ...c, subcategorias: Math.max((c.subcategorias || 0) - 1, 0) };
              if (c._id === d.category) return { ...c, subcategorias: (c.subcategorias || 0) + 1 };
              return c;
            });
          }
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
      }
      setDialogSub(null); setErrores({});
      setToast({ tipo: 'success', msg: d.modo === 'crear' ? 'Subcategoría creada.' : 'Subcategoría actualizada.' });
    } catch (e) {
      const msg = e.response?.data?.errors?.[0]?.msg || 'No se pudo guardar.';
      setErrores({ name: msg });
    } finally {
      setGuardando(false);
    }
  };

  const cargaMasiva = async () => {
    const d = dialogBulk;
    const esSub = d.tipo === 'subcategorias';
    const nombres = d.texto.split('\n').map(s => s.trim()).filter(Boolean);
    if (!nombres.length) { setErrores({ bulk: `Escribe al menos una ${esSub ? 'subcategoría' : 'categoría'}` }); return; }
    if (esSub && !d.category) { setErrores({ categoryBulk: 'Selecciona una categoría' }); return; }
    setGuardando(true);
    try {
      if (esSub) {
        const res = await adminAxios.post('/api/admin/catalogo/subcategorias/bulk', { category: d.category, nombres });
        const { creadas, omitidas } = res.data;
        const nuevas = res.data.subcategorias || [];
        setData(prev => {
          const subcategorias = [...prev.subcategorias, ...nuevas];
          const categorias = prev.categorias.map(c => c._id === d.category
            ? { ...c, subcategorias: (c.subcategorias || 0) + nuevas.length } : c);
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
        setDialogBulk(null); setErrores({});
        setToast({ tipo: 'success', msg: `${creadas} subcategoría(s) creada(s)${omitidas ? `, ${omitidas} omitida(s) por duplicado` : ''}.` });
      } else {
        const res = await adminAxios.post('/api/admin/catalogo/categorias/bulk', { nombres, color: d.color });
        const { creadas, omitidas } = res.data;
        const nuevas = (res.data.categorias || []).map(c => ({ ...c, subcategorias: 0 }));
        setData(prev => {
          const categorias = [...prev.categorias, ...nuevas];
          return { ...prev, categorias, stats: recomputarStats(categorias, prev.subcategorias) };
        });
        setDialogBulk(null); setErrores({});
        setToast({ tipo: 'success', msg: `${creadas} categoría(s) creada(s)${omitidas ? `, ${omitidas} omitida(s) por duplicado` : ''}.` });
      }
    } catch (e) {
      setErrores({ bulk: e.response?.data?.msg || 'No se pudo cargar.' });
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    const { tipo, item } = confirmDel;
    setConfirmDel(null);
    try {
      if (tipo === 'categoria') {
        const res = await adminAxios.delete(`/api/category/${item._id}`);
        const eliminada = res.data.modo === 'eliminada';
        setData(prev => {
          let categorias, subcategorias;
          if (eliminada) {
            // Borrado real: se van la categoría y sus subcategorías.
            categorias = prev.categorias.filter(c => c._id !== item._id);
            subcategorias = prev.subcategorias.filter(s => String(s.category) !== String(item._id));
          } else {
            // Baja lógica en cascada.
            categorias = prev.categorias.map(c => c._id === item._id ? { ...c, status: false } : c);
            subcategorias = prev.subcategorias.map(s => String(s.category) === String(item._id) ? { ...s, status: false } : s);
          }
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
        setToast({ tipo: 'success', msg: eliminada ? 'Categoría eliminada por completo.' : 'Categoría en uso: se desactivó (baja lógica).' });
      } else {
        const res = await adminAxios.delete(`/api/category/subcategory/${item._id}`);
        const eliminada = res.data.modo === 'eliminada';
        setData(prev => {
          let subcategorias, categorias = prev.categorias;
          if (eliminada) {
            // Borrado real: se quita la fila y baja el conteo de su categoría.
            subcategorias = prev.subcategorias.filter(s => s._id !== item._id);
            categorias = prev.categorias.map(c =>
              String(c._id) === String(item.category)
                ? { ...c, subcategorias: Math.max((c.subcategorias || 0) - 1, 0) }
                : c
            );
          } else {
            subcategorias = prev.subcategorias.map(s => s._id === item._id ? { ...s, status: false } : s);
          }
          return { ...prev, categorias, subcategorias, stats: recomputarStats(categorias, subcategorias) };
        });
        setToast({ tipo: 'success', msg: eliminada ? 'Subcategoría eliminada por completo.' : 'Subcategoría en uso: se desactivó (baja lógica).' });
      }
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo eliminar.' });
    }
  };

  /* ---------- Render ---------- */

  const cabeceraSx = { color: '#94A3B8', fontWeight: 700, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', borderBottom: '1px solid #F1F5F9' };
  const celdaSx = { borderBottom: '1px solid #F6F8FB', py: 1.5 };

  return (
    <Box>
      {/* Botones de acción */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<FileUploadOutlinedIcon />}
          onClick={() => {
            setErrores({});
            if (tab === 'categorias') setDialogBulk({ tipo: 'categorias', texto: '', color: PALETA[1] });
            else setDialogBulk({ tipo: 'subcategorias', texto: '', category: data.categorias[0]?._id || '' });
          }}
          sx={{ bgcolor: '#D6F3E8', color: '#1B7A5A', fontWeight: 700, borderRadius: 2, px: 2.5, '&:hover': { bgcolor: '#C2EBDA' } }}>
          Carga masiva
        </Button>
        <Button variant="contained" startIcon={<AddRoundedIcon />}
          onClick={() => {
            setErrores({});
            if (tab === 'categorias') setDialogCat({ modo: 'crear', name: '', descripcion: '', color: PALETA[1], requiereCedula: false });
            else setDialogSub({ modo: 'crear', name: '', descripcion: '', category: data.categorias[0]?._id || '' });
          }}
          sx={{ bgcolor: 'primary.main', fontWeight: 700, borderRadius: 2, px: 2.5 }}>
          {tab === 'categorias' ? 'Nueva Categoría' : 'Nueva Subcategoría'}
        </Button>
      </Box>

      {/* Tarjetas resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard icon={<GridViewOutlinedIcon />} bg="#EEF2FF" color="secondary.main"
            label="Total categorías" valor={stats?.totalCategorias ?? '—'} pie={stats ? `${stats.categoriasActivas} activas` : ''} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard icon={<CheckCircleOutlineOutlinedIcon />} bg="#D6F3E8" color="#1B7A5A"
            label="Categorías activas" valor={stats?.categoriasActivas ?? '—'} pie={stats ? `${stats.totalCategorias - stats.categoriasActivas} inactivas` : ''} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard icon={<FormatListBulletedOutlinedIcon />} bg="#E6F0FF" color="info.main"
            label="Total subcategorías" valor={stats?.totalSubcategorias ?? '—'} pie={stats ? `en ${stats.totalCategorias} categorías` : ''} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard icon={<CheckRoundedIcon />} bg="#FFF1D6" color="#A8740F"
            label="Subcats. activas" valor={stats?.subcategoriasActivas ?? '—'} pie={stats ? `${stats.totalSubcategorias - stats.subcategoriasActivas} inactivas` : ''} />
        </Grid>
      </Grid>

      {/* Tabla */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
        {/* Tabs + búsqueda */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, p: 2.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, bgcolor: '#F8FAFC', p: 0.5, borderRadius: 2.5 }}>
            {[
              { key: 'categorias', label: 'Categorías', count: stats?.totalCategorias },
              { key: 'subcategorias', label: 'Subcategorías', count: stats?.totalSubcategorias },
            ].map(t => {
              const activo = tab === t.key;
              return (
                <Box key={t.key} onClick={() => { setTab(t.key); setQ(''); setOrden({ campo: 'name', dir: 'asc' }); }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1, borderRadius: 2, cursor: 'pointer',
                    bgcolor: activo ? 'white' : 'transparent', boxShadow: activo ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    color: activo ? 'secondary.main' : '#737791', fontWeight: 600, transition: 'all .15s',
                  }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t.label}</Typography>
                  <Chip label={t.count ?? 0} size="small"
                    sx={{ height: 20, minWidth: 24, bgcolor: activo ? '#EEF2FF' : '#EEF1F6', color: activo ? 'secondary.main' : '#94A3B8', fontWeight: 700 }} />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#F8FAFC', border: '1px solid #F1F5F9', borderRadius: 3, px: 2, py: 1, minWidth: 240 }}>
            <SearchOutlinedIcon sx={{ color: '#94A3B8' }} fontSize="small" />
            <InputBase placeholder="Buscar..." value={q} onChange={e => setQ(e.target.value)} sx={{ fontSize: 14, flex: 1 }} />
          </Box>
        </Box>

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : tab === 'categorias' ? (
          <Table>
            <TableHead>
              <TableRow>
                <HeaderOrden campo="color">Color</HeaderOrden>
                <HeaderOrden campo="name">Nombre</HeaderOrden>
                <TableCell sx={cabeceraSx}>Descripción</TableCell>
                <TableCell sx={cabeceraSx} align="center">Subcategorías</TableCell>
                <TableCell sx={cabeceraSx} align="center">Cédula prof.</TableCell>
                <TableCell sx={cabeceraSx} align="center">Estado</TableCell>
                <TableCell sx={cabeceraSx} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categoriasOrdenadas.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6, color: '#94A3B8', border: 0 }}>No hay categorías.</TableCell></TableRow>
              ) : categoriasPagina.map((c, i) => {
                const color = colorPorCategoria[c._id] || colorDe(c, i);
                return (
                  <TableRow key={c._id} hover>
                    <TableCell sx={celdaSx}>
                      <Avatar variant="rounded" sx={{ bgcolor: color, width: 40, height: 40 }}>
                        <SellOutlinedIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Avatar>
                    </TableCell>
                    <TableCell sx={celdaSx}>
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{c.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...celdaSx, maxWidth: 360 }}>
                      <Typography variant="body2" sx={{ color: c.descripcion ? '#737791' : '#CBD5E1' }}>
                        {c.descripcion || 'Sin descripción'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={celdaSx} align="center">
                      <Chip label={c.subcategorias} size="small" sx={{ bgcolor: '#EEF2FF', color: 'secondary.main', fontWeight: 700, minWidth: 32 }} />
                    </TableCell>
                    <TableCell sx={celdaSx} align="center">
                      <Tooltip title={c.requiereCedula ? 'Exige cédula profesional aprobada para publicar servicios' : 'No exige cédula profesional'}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                          <WorkspacePremiumOutlinedIcon sx={{ fontSize: 18, color: c.requiereCedula ? 'secondary.main' : '#CBD5E1' }} />
                          <Switch checked={!!c.requiereCedula} onChange={() => toggleCedula(c)} color="primary" size="small" />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={celdaSx} align="center"><EstadoChip activo={c.status} /></TableCell>
                    <TableCell sx={celdaSx} align="right">
                      <Tooltip title={c.status ? 'Desactivar' : 'Activar'}>
                        <Switch checked={!!c.status} onChange={() => toggleCategoria(c)} color="success" size="small" />
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setErrores({}); setDialogCat({ modo: 'editar', _id: c._id, name: c.name, descripcion: c.descripcion || '', color, requiereCedula: !!c.requiereCedula }); }}>
                          <EditOutlinedIcon fontSize="small" sx={{ color: '#7495F8' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Dar de baja">
                        <IconButton size="small" onClick={() => setConfirmDel({ tipo: 'categoria', item: c })}>
                          <DeleteOutlineOutlinedIcon fontSize="small" sx={{ color: '#FF827A' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <HeaderOrden campo="name">Nombre</HeaderOrden>
                <HeaderOrden campo="categoria">Categoría</HeaderOrden>
                <TableCell sx={cabeceraSx}>Descripción</TableCell>
                <TableCell sx={cabeceraSx} align="center">Estado</TableCell>
                <TableCell sx={cabeceraSx} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subcategoriasOrdenadas.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: '#94A3B8', border: 0 }}>No hay subcategorías.</TableCell></TableRow>
              ) : subcategoriasPagina.map((s) => (
                <TableRow key={s._id} hover>
                  <TableCell sx={celdaSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorPorCategoria[s.category] || '#CBD5E1' }} />
                      <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>{s.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={celdaSx}>
                    <Chip label={s.categoriaNombre} size="small" variant="outlined" sx={{ borderColor: '#E2E8F0', color: '#475569' }} />
                  </TableCell>
                  <TableCell sx={{ ...celdaSx, maxWidth: 360 }}>
                    <Typography variant="body2" sx={{ color: s.descripcion ? '#737791' : '#CBD5E1' }}>{s.descripcion || 'Sin descripción'}</Typography>
                  </TableCell>
                  <TableCell sx={celdaSx} align="center"><EstadoChip activo={s.status} /></TableCell>
                  <TableCell sx={celdaSx} align="right">
                    <Tooltip title={s.status ? 'Desactivar' : 'Activar'}>
                      <Switch checked={!!s.status} onChange={() => toggleSubcategoria(s)} color="success" size="small" />
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => { setErrores({}); setDialogSub({ modo: 'editar', _id: s._id, name: s.name, descripcion: s.descripcion || '', category: s.category || '', categoryOriginal: s.category || '' }); }}>
                        <EditOutlinedIcon fontSize="small" sx={{ color: '#7495F8' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Dar de baja">
                      <IconButton size="small" onClick={() => setConfirmDel({ tipo: 'subcategoria', item: s })}>
                        <DeleteOutlineOutlinedIcon fontSize="small" sx={{ color: '#FF827A' }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!cargando && totalActual > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, px: 2.5, py: 3 }}>
            {totalPaginas > 1 && (
              <Pagination
                count={totalPaginas}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                shape="rounded"
                size="large"
                sx={{ '& .MuiPaginationItem-root': { fontSize: 15, minWidth: 38, height: 38 } }}
              />
            )}
            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
              Mostrando {inicio + 1}–{Math.min(inicio + LIMIT, totalActual)} de {totalActual} {tab === 'categorias' ? 'categorías' : 'subcategorías'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ---------- Diálogo Categoría ---------- */}
      <Dialog open={!!dialogCat} onClose={() => setDialogCat(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <CabeceraDialog
          icon={<SellOutlinedIcon />}
          titulo={dialogCat?.modo === 'crear' ? 'Nueva categoría' : 'Editar categoría'}
          sub={dialogCat?.modo === 'crear' ? 'Crea una categoría del marketplace' : 'Modifica los datos de la categoría'}
          onClose={() => setDialogCat(null)} />
        <DialogContent sx={{ p: 3 }}>
          {/* Vista previa en vivo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
            <Avatar variant="rounded" sx={{ bgcolor: dialogCat?.color, width: 44, height: 44 }}>
              <SellOutlinedIcon sx={{ color: 'white' }} />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: 'primary.main' }} noWrap>
                {dialogCat?.name?.trim() || 'Nombre de la categoría'}
              </Typography>
              <Typography variant="caption" sx={{ color: dialogCat?.descripcion ? '#737791' : '#CBD5E1' }} noWrap>
                {dialogCat?.descripcion?.trim() || 'Sin descripción'}
              </Typography>
            </Box>
          </Box>

          <TextField fullWidth label="Nombre" value={dialogCat?.name || ''} autoFocus margin="dense"
            error={!!errores.name} helperText={errores.name}
            onChange={e => setDialogCat(d => ({ ...d, name: e.target.value }))} />
          <TextField fullWidth label="Descripción" value={dialogCat?.descripcion || ''} margin="dense" multiline minRows={2}
            placeholder="Breve descripción de la categoría" onChange={e => setDialogCat(d => ({ ...d, descripcion: e.target.value }))} />

          <Typography variant="caption" sx={{ color: '#737791', mt: 2.5, mb: 1, display: 'block', fontWeight: 700 }}>COLOR DE LA ETIQUETA</Typography>
          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
            {PALETA.map(col => (
              <Box key={col} onClick={() => setDialogCat(d => ({ ...d, color: col }))}
                sx={{
                  width: 34, height: 34, borderRadius: 2, bgcolor: col, cursor: 'pointer', transition: 'transform .12s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { transform: 'scale(1.1)' },
                  outline: dialogCat?.color === col ? '2px solid #0E2465' : '2px solid transparent', outlineOffset: 2,
                }}>
                {dialogCat?.color === col && <CheckRoundedIcon sx={{ color: 'white', fontSize: 18 }} />}
              </Box>
            ))}
          </Box>

          {/* Exigir cédula profesional para esta categoría */}
          <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: dialogCat?.requiereCedula ? '#EEF3FF' : '#F8FAFC', border: '1px solid', borderColor: dialogCat?.requiereCedula ? 'secondary.main' : '#F1F5F9', transition: 'all .15s' }}>
            <FormControlLabel
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={<Switch checked={!!dialogCat?.requiereCedula} onChange={e => setDialogCat(d => ({ ...d, requiereCedula: e.target.checked }))} color="primary" />}
              label={
                <Box sx={{ ml: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <WorkspacePremiumOutlinedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                    <Typography sx={{ fontWeight: 700, color: 'primary.main' }}>Requiere cédula profesional</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#737791' }}>
                    Los servicios de esta categoría (ej. Abogado, Médico) no se publican ni se pueden contratar hasta que el proveedor suba una cédula y el equipo la apruebe.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button onClick={() => setDialogCat(null)} sx={{ color: '#737791', fontWeight: 600 }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarCategoria} disabled={guardando} sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}>
            {guardando ? 'Guardando…' : dialogCat?.modo === 'crear' ? 'Crear categoría' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Diálogo Subcategoría ---------- */}
      <Dialog open={!!dialogSub} onClose={() => setDialogSub(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <CabeceraDialog
          icon={<AccountTreeOutlinedIcon />}
          titulo={dialogSub?.modo === 'crear' ? 'Nueva subcategoría' : 'Editar subcategoría'}
          sub={dialogSub?.modo === 'crear' ? 'Crea una subcategoría dentro de una categoría' : 'Modifica los datos de la subcategoría'}
          onClose={() => setDialogSub(null)} />
        <DialogContent sx={{ p: 3 }}>
          {/* Vista previa en vivo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, mb: 2.5, borderRadius: 2, bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: colorPorCategoria[dialogSub?.category] || '#CBD5E1', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: 'primary.main' }} noWrap>
                {dialogSub?.name?.trim() || 'Nombre de la subcategoría'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#737791' }} noWrap>
                {data.categorias.find(c => c._id === dialogSub?.category)?.name || 'Sin categoría'}
              </Typography>
            </Box>
          </Box>

          <TextField fullWidth select label="Categoría" value={dialogSub?.category || ''} margin="dense"
            error={!!errores.category} helperText={errores.category}
            onChange={e => setDialogSub(d => ({ ...d, category: e.target.value }))}>
            {data.categorias.map((c, i) => (
              <MenuItem key={c._id} value={c._id}>
                <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: colorPorCategoria[c._id] || colorDe(c, i), mr: 1.2, verticalAlign: 'middle' }} />
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="Nombre" value={dialogSub?.name || ''} margin="dense"
            error={!!errores.name} helperText={errores.name}
            onChange={e => setDialogSub(d => ({ ...d, name: e.target.value }))} />
          <TextField fullWidth label="Descripción" value={dialogSub?.descripcion || ''} margin="dense" multiline minRows={2}
            placeholder="Breve descripción de la subcategoría" onChange={e => setDialogSub(d => ({ ...d, descripcion: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button onClick={() => setDialogSub(null)} sx={{ color: '#737791', fontWeight: 600 }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarSubcategoria} disabled={guardando} sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}>
            {guardando ? 'Guardando…' : dialogSub?.modo === 'crear' ? 'Crear subcategoría' : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Diálogo Carga masiva ---------- */}
      <Dialog open={!!dialogBulk} onClose={() => setDialogBulk(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <CabeceraDialog
          icon={<FileUploadOutlinedIcon />}
          titulo={dialogBulk?.tipo === 'subcategorias' ? 'Carga masiva de subcategorías' : 'Carga masiva de categorías'}
          sub={dialogBulk?.tipo === 'subcategorias' ? 'Elige la categoría y agrega varias subcategorías' : 'Agrega varias categorías de una vez'}
          onClose={() => setDialogBulk(null)} />
        <DialogContent sx={{ p: 3 }}>
          {dialogBulk?.tipo === 'subcategorias' && (
            <TextField fullWidth select label="Categoría" value={dialogBulk?.category || ''} margin="dense"
              error={!!errores.categoryBulk} helperText={errores.categoryBulk}
              onChange={e => setDialogBulk(d => ({ ...d, category: e.target.value }))}>
              {data.categorias.map((c, i) => (
                <MenuItem key={c._id} value={c._id}>
                  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', bgcolor: colorPorCategoria[c._id] || colorDe(c, i), mr: 1.2, verticalAlign: 'middle' }} />
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" sx={{ color: '#737791', mt: dialogBulk?.tipo === 'subcategorias' ? 2 : 0, mb: 1.5 }}>
            Escribe {dialogBulk?.tipo === 'subcategorias' ? 'una subcategoría' : 'una categoría'} por línea. Se omiten las vacías y las que ya existen.
          </Typography>
          <TextField fullWidth multiline minRows={6}
            placeholder={dialogBulk?.tipo === 'subcategorias' ? 'Corte de cabello\nManicure\nMaquillaje' : 'Belleza y Estética\nTecnología\nHogar y Reparaciones'}
            value={dialogBulk?.texto || ''} error={!!errores.bulk} helperText={errores.bulk}
            onChange={e => setDialogBulk(d => ({ ...d, texto: e.target.value }))} />
          {dialogBulk?.tipo === 'categorias' && (
            <>
              <Typography variant="caption" sx={{ color: '#737791', mt: 2.5, mb: 1, display: 'block', fontWeight: 700 }}>COLOR PARA TODAS</Typography>
              <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap' }}>
                {PALETA.map(col => (
                  <Box key={col} onClick={() => setDialogBulk(d => ({ ...d, color: col }))}
                    sx={{
                      width: 32, height: 32, borderRadius: 2, bgcolor: col, cursor: 'pointer', transition: 'transform .12s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { transform: 'scale(1.1)' },
                      outline: dialogBulk?.color === col ? '2px solid #0E2465' : '2px solid transparent', outlineOffset: 2,
                    }}>
                    {dialogBulk?.color === col && <CheckRoundedIcon sx={{ color: 'white', fontSize: 18 }} />}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button onClick={() => setDialogBulk(null)} sx={{ color: '#737791', fontWeight: 600 }}>Cancelar</Button>
          <Button variant="contained" onClick={cargaMasiva} disabled={guardando} sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}>
            {guardando ? 'Cargando…' : 'Cargar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- Confirmar baja ---------- */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Eliminar</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar <strong>{confirmDel?.item?.name}</strong>?
            {confirmDel?.tipo === 'categoria' && ' Incluye sus subcategorías.'}
          </Typography>
          <Typography sx={{ mt: 1.5, color: '#737791', fontSize: 13 }}>
            Si no está siendo usada por ningún servicio o página, se elimina por completo (permanente).
            Si está en uso, solo se desactiva (baja lógica, reversible).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDel(null)} sx={{ color: '#737791' }}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={eliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Catalogo;
